import {
  ApproveSupplierPrepaymentApContractorParams,
  BankDocumentEntryAllocationInputDto,
  PostBankDocumentBatchPayload,
  PostBankDocumentEntryPayload,
  RegisterSupplierPrepaymentPayload,
  UnpostBankDocumentEntryPayload,
  UpdateBankDocumentEntryRequest,
} from '../Types/bankDocument.types';

interface BuildPostBankDocumentEntryRequestArgs {
  entryId: number;
  userId: number;
  idempotencyKey?: string;
}

export const buildPostBankDocumentEntryPayload = (
  entryId: number,
  userId: number,
  idempotencyKey?: string
): PostBankDocumentEntryPayload => ({
  entryId,
  postedByUserId: userId,
  idempotencyKey,
});

export const buildPostBankDocumentEntryRequest = ({
  entryId,
  userId,
  idempotencyKey,
}: BuildPostBankDocumentEntryRequestArgs): PostBankDocumentEntryPayload =>
  buildPostBankDocumentEntryPayload(entryId, userId, idempotencyKey);

interface BuildUnpostBankDocumentEntryRequestArgs {
  entryId: number;
  bankDocumentId: number;
  idempotencyKey?: string;
}

export const buildUnpostBankDocumentEntryRequest = ({
  entryId,
  bankDocumentId,
  idempotencyKey,
}: BuildUnpostBankDocumentEntryRequestArgs): UnpostBankDocumentEntryPayload => ({
  entryId,
  bankDocumentId,
  idempotencyKey,
});

interface BuildRegisterSupplierPrepaymentRequestArgs {
  entryId: number;
  userId: number;
  supplierApContractorId: number;
  description: string;
}

const mapPostingDescription = (postingDescription: string): string | undefined => {
  const trimmedValue = postingDescription.trim();
  return trimmedValue || undefined;
};

export const buildRegisterSupplierPrepaymentRequest = ({
  entryId,
  userId,
  supplierApContractorId,
  description,
}: BuildRegisterSupplierPrepaymentRequestArgs): RegisterSupplierPrepaymentPayload => ({
  entryId,
  createdByUserId: userId,
  supplierApContractorId,
  description: mapPostingDescription(description),
});

export const buildPostBankDocumentBatchPayload = (
  bankDocumentId: number,
  userId: number
): PostBankDocumentBatchPayload => ({
  bankDocumentId,
  postedByUserId: userId,
});

export const buildApproveSupplierPrepaymentApContractorRequest = (
  apContractorId: number
): ApproveSupplierPrepaymentApContractorParams => ({
  apContractorId,
});

export type UpdateBankDocumentEntryFields = Omit<
  UpdateBankDocumentEntryRequest,
  'entryId' | 'bankDocumentId'
>;

export const normalizeMoneyToEven2dp = (value: number): number => {
  const factor = 100;
  const scaledValue = value * factor;
  const absoluteScaled = Math.abs(scaledValue);
  const integerPart = Math.floor(absoluteScaled);
  const fractionalPart = absoluteScaled - integerPart;
  const epsilon = 1e-8;
  let roundedIntegerPart = integerPart;

  if (fractionalPart > 0.5 + epsilon) {
    roundedIntegerPart = integerPart + 1;
  } else if (Math.abs(fractionalPart - 0.5) <= epsilon) {
    roundedIntegerPart = integerPart % 2 === 0 ? integerPart : integerPart + 1;
  }

  const roundedScaled = roundedIntegerPart * Math.sign(scaledValue || 1);
  return roundedScaled / factor;
};

const normalizeAllocations = (
  allocations: BankDocumentEntryAllocationInputDto[]
): BankDocumentEntryAllocationInputDto[] =>
  allocations.map((allocation) => ({
    targetType: allocation.targetType,
    targetId: allocation.targetId,
    allocatedAmount: normalizeMoneyToEven2dp(allocation.allocatedAmount),
  }));

export const buildUpdateBankDocumentEntryRequest = (
  entryId: number,
  bankDocumentId: number,
  fields: UpdateBankDocumentEntryFields
): UpdateBankDocumentEntryRequest => {
  if (fields.allocations !== undefined && fields.allocations !== null) {
    return {
      entryId,
      bankDocumentId,
      ...fields,
      invoiceId: null,
      reservationId: null,
      expenseInvoiceId: null,
      allocations: normalizeAllocations(fields.allocations),
    };
  }

  return {
    entryId,
    bankDocumentId,
    ...fields,
  };
};
